import "vue3-carousel/carousel.css";
import { Carousel, Slide, Navigation, CarouselConfig } from "vue3-carousel";
import type {
	IComponent,
	IComponentStyle,
	IModelMessageSettings,
	IModelVue3SlideEnd,
	IStyleComponentItem,
} from "../types";
import { Component, Vue, Prop } from "vue-facing-decorator";
import type { TypeLayout, TypeStructure } from "../enums";
import ListEvent from "../components/list-event.vue";

@Component({
	components: {
		Carousel,
		Slide,
		Navigation,
		ListEvent,
	},
})
export default class LayoutCarousel extends Vue {
	@Prop({ type: String, default: "bubble" })
	type?: TypeStructure;

	@Prop({ type: String, default: "carousel" })
	layout?: TypeLayout;

	@Prop({
		type: Array,
		default() {
			return [];
		},
	})
	components?: IComponent[];

	@Prop({
		type: Object,
		default() {
			return {};
		},
	})
	settings?: IModelMessageSettings;

	// State properties
	currentIndex: number = 0; // Tracks the current slide index

	// Dynamically calculated arrow visibility
	get showPrev() {
		const totalSlides = this.components.length; // Assuming `components` is the slide data array
		return this.currentIndex > 0 && totalSlides !== 1; // Show if not on the first slide
	}

	get showNext() {
		const totalSlides = this.components.length; // Assuming `components` is the slide data array
		return this.currentIndex < totalSlides - 1 && totalSlides !== 1; // Show if not on the last slide
	}

	get componentSettings() {
		return typeof this.settings === "object" && !Array.isArray(this.settings) ? this.settings : {};
	}

	/**
	 * Computes and returns the overall style configuration for the component.
	 */
	get componentStyle() {
		let componentItems: IStyleComponentItem = {};
		if (
			this.settings.carouselSettings &&
			this.settings.carouselSettings.containerSettings &&
			this.settings.carouselSettings.containerSettings.componentItems
		) {
			componentItems = this.settings.carouselSettings.containerSettings.componentItems;
		}

		let style: IComponentStyle = {};
		const elementStyle = componentItems["carouselContainer"];
		if (elementStyle && typeof elementStyle === "object" && !Array.isArray(elementStyle)) style = elementStyle;

		return style;
	}

	/**
	 * Computes and returns the style object for the scrollable view.
	 *
	 * This method determines the height of the container dynamically based on the chat body height
	 * when using a "list" layout (except for modals). If no explicit height or maxHeight is set,
	 * it defaults to 70% of the `divChatBody` height.
	 *
	 * @returns {IComponentStyle} An object containing style properties such as `width`, `height`,
	 * `borderRadius`, `backgroundColor`, `margin`, `padding`, `maxHeight`, and `maxWidth`.
	 *
	 */
	get styleScrollView() {
		let style: IComponentStyle = {};
		/**
		 * JIRA: SD-7488
		 * Carousel - Fixed vertical height with scroll for List-Bubble and List-Card
		 *
		 * Adjusts the max height of the carousel list container based on the chat body height.
		 *
		 * The below statement checks the this.layout is "list" and this.type not "modal" .
		 * If the required elements exist (`carouselListContainer` and `divChatBody`),
		 * it sets the `maxHeight` of `listContainer` to 70% of `divChatBody`'s height.
		 *
		 */
		if (this.componentStyle && typeof this.componentStyle === "object" && !Array.isArray(this.componentStyle))
			style = this.componentStyle;
		const { width, maxWidth, height = "auto", backgroundColor, margin, padding, background } = style;
		let { maxHeight, borderRadius } = style;

		// Apply default border-radius for list layout (excluding modals)
		if (this.layout === "list" && this.type !== "modal") {
			borderRadius = "12px";
		}

		const divChatBody = document.getElementById("divChatBody") as HTMLDivElement;
		if (!maxHeight && this.layout === "list" && this.type !== "modal" && divChatBody && divChatBody.offsetHeight) {
			maxHeight = `${divChatBody.offsetHeight * 0.7}px`;
		}

		return {
			background: background || backgroundColor,
			borderRadius,
			margin,
			padding,
			width,
			height,
			maxHeight,
			maxWidth,
		};
	}

	/**
	 * JIRA: SD-8156
	 * Carousel configuration setting - Referring from https://vue3-carousel.ismail9k.com/config.html
	 * */
	carouselSettings: CarouselConfig = {
		itemsToShow: 1.2,
		wrapAround: false,
		enabled: true,
		gap: 0,
		height: "100%",
		ignoreAnimations: true,
		snapAlign: "center",
		autoplay: 0,
		i18n: {},
		itemsToScroll: 1,
		slideEffect: "slide",
		breakpointMode: "carousel",
		clamp: false,
		dir: "ltr",
		preventExcessiveDragging: false,
	};

	/* Navigates to the next slide in the carousel. */
	navNext() {
		const carouselContainer = this.$refs.carouselContainer as any;
		if (carouselContainer) {
			carouselContainer.next();
		}
	}

	/* Navigates to the previous slide in the carousel. */
	navPrev() {
		const carouselContainer = this.$refs.carouselContainer as any;
		if (carouselContainer) {
			carouselContainer.prev();
		}
	}

	/* Carousel Change observer to find the index of the carousel */
	onChangeCarousel({ currentSlideIndex }: IModelVue3SlideEnd) {
		this.currentIndex = currentSlideIndex;
	}
}
